// Generates a self-contained "skill" (instructions) the user hands to THEIR OWN AI so it can
// update a Niobe artefact by talking to the passerelle's /bridge endpoints — without invoking the
// built-in Claude/Copilot chat. The skill embeds the passerelle URL, the auth token, the payload
// format, and the task. For use when the user prefers an AI other than Claude or Copilot.

export interface SkillPart {
  room: string
  title: string
}

/**
 * The passerelle's absolute base URL (an external agent cannot use the browser's dev proxy).
 * Defaults to the dev port 4100 on the current host; override with VITE_PASSERELLE_URL.
 */
export function passerelleBaseUrl(): string {
  const env = (import.meta.env.VITE_PASSERELLE_URL as string | undefined)?.replace(/\/$/, '')
  if (env) return env
  return `${window.location.protocol}//${window.location.hostname}:4100`
}

export function buildBridgeSkill(o: {
  baseUrl: string
  bid: string
  artefactId: string
  title: string
  token: string
  parts: SkillPart[]
}): string {
  const getUrl = `${o.baseUrl}/bridge/artefact?bid=${encodeURIComponent(o.bid)}&artefactId=${encodeURIComponent(o.artefactId)}`
  const putUrl = `${o.baseUrl}/bridge/artefact`
  const partsList = o.parts.length
    ? o.parts.map((p) => `  - \`${p.room}\` — ${p.title}`).join('\n')
    : '  (no parts yet)'

  return `# Niobe skill — update "${o.title}" with your own AI

You can update this artefact with **any AI/tool that can make HTTP requests** (not the built-in
Claude/Copilot chat). Follow the three steps below. The passerelle applies your changes on the
server exactly as the Niobe UI would (each change is recorded in the part's modification history).
It does **not** call any platform AI.

- Artefact: **${o.title}**
- Bid: \`${o.bid}\`  ·  Artefact id: \`${o.artefactId}\`
- Current parts:
${partsList}

Authenticate every request with this header:

    Authorization: Bearer ${o.token}

## 1) Fetch the current structure

    GET ${getUrl}

Response:

    {
      "bid": "${o.bid}",
      "artefactId": "${o.artefactId}",
      "title": "${o.title}",
      "parts": [
        { "partId": "...", "room": "${o.bid}/${o.artefactId}/<part>", "title": "...", "markdown": "..." }
      ]
    }

## 2) Edit the markdown

Edit the \`markdown\` of any part. You may add new parts. You **cannot** delete parts and you
**cannot** create new artefacts.

## 3) Send everything back

    PUT ${putUrl}
    Content-Type: application/json
    Authorization: Bearer ${o.token}

Body — return ALL parts (changed or not), each with an \`action\`:

    {
      "bid": "${o.bid}",
      "artefactId": "${o.artefactId}",
      "parts": [
        { "room": "${o.bid}/${o.artefactId}/<part>", "title": "...", "markdown": "<updated>", "action": "update" },
        { "title": "A brand new part", "markdown": "<content>", "action": "create" },
        { "room": "${o.bid}/${o.artefactId}/<part>", "title": "...", "markdown": "<unchanged>", "action": "unchanged" }
      ]
    }

- \`action: "update"\`   → replace the part's markdown (and rename it if the title changed)
- \`action: "create"\`   → add a new part (provide \`title\` + \`markdown\`, no \`room\`)
- \`action: "unchanged"\` → leave the part as-is

Response: \`{ "applied": [...], "skipped": [...] }\`.

## What I want

> Describe here what you want changed in this document.

---
_Note: the Bearer token above is your current Niobe session token — treat it like a password._
`
}
